import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { AlertaPrimeirosAtendimentos } from '../components/primeiros/AlertaPrimeirosAtendimentos';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Search, User, Calendar, Stethoscope, Loader2, Activity, Plus,
  FileText, Trash2, Pencil, AlertTriangle, Clock, X, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ESPECIALIDADES = [
  { key: 'fisio', label: 'Fisioterapia', color: 'text-green-600', border: 'border-green-300 text-green-700', bg: 'bg-green-50' },
  { key: 'fono', label: 'Fonoaudiologia', color: 'text-purple-600', border: 'border-purple-300 text-purple-700', bg: 'bg-purple-50' },
  { key: 'nutri', label: 'Nutrição', color: 'text-orange-600', border: 'border-orange-300 text-orange-700', bg: 'bg-orange-50' },
  { key: 'psicologo', label: 'Psicologia', color: 'text-blue-600', border: 'border-blue-300 text-blue-700', bg: 'bg-blue-50' },
  { key: 'clinico', label: 'Clínico', color: 'text-rose-600', border: 'border-rose-300 text-rose-700', bg: 'bg-rose-50' },
  { key: 'enfermeiro', label: 'Enfermeiro(a)', color: 'text-teal-600', border: 'border-teal-300 text-teal-700', bg: 'bg-teal-50' },
];

const STATUS_LABELS = {
  admissao_pendente: 'Admissão Pendente',
  em_tratamento: 'Em Tratamento',
  triagem: 'Triagem',
  ativo_estavel: 'Ativo Estável',
  ativo_atencao: 'Ativo Atenção',
  alta_planejada: 'Alta Planejada',
};

const Fisioterapia = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fisio');
  const [data, setData] = useState({ pacientes: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [profissionalFilter, setProfissionalFilter] = useState('');
  const [profissionais, setProfissionais] = useState([]);

  // Evolution panel state
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [evolucoes, setEvolucoes] = useState([]);
  const [loadingEvolucoes, setLoadingEvolucoes] = useState(false);
  const [novaEvolucao, setNovaEvolucao] = useState({ texto: '', data_sessao: new Date().toISOString().split('T')[0] });
  const [salvandoEvolucao, setSalvandoEvolucao] = useState(false);
  const [editingEvolucao, setEditingEvolucao] = useState(null);

  // Alerts state
  const [alertas, setAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);

  // Report state
  const [relatorio, setRelatorio] = useState(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);

  const fetchPacientes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/fisioterapia/pacientes?especialidade=${activeTab}`);
      setData(res.data);
      setProfissionais(res.data.profissionais || []);
    } catch (err) {
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  }, [api, activeTab]);

  const fetchAlertas = useCallback(async () => {
    setLoadingAlertas(true);
    try {
      const res = await api.get(`/fisioterapia/alertas?especialidade=${activeTab}`);
      setAlertas(res.data.alertas || []);
    } catch { /* ignore */ }
    finally { setLoadingAlertas(false); }
  }, [api, activeTab]);

  useEffect(() => {
    fetchPacientes();
    fetchAlertas();
    setSelectedPaciente(null);
  }, [fetchPacientes, fetchAlertas]);

  // Fetch evolucoes when a patient is selected
  const fetchEvolucoes = useCallback(async (internacaoId) => {
    setLoadingEvolucoes(true);
    try {
      const res = await api.get(`/fisioterapia/evolucoes/${internacaoId}?especialidade=${activeTab}`);
      setEvolucoes(res.data.evolucoes || []);
    } catch { setEvolucoes([]); }
    finally { setLoadingEvolucoes(false); }
  }, [api, activeTab]);

  useEffect(() => {
    if (selectedPaciente) {
      fetchEvolucoes(selectedPaciente.internacao_id);
    }
  }, [selectedPaciente, fetchEvolucoes]);

  const handleCriarEvolucao = async () => {
    if (!novaEvolucao.texto.trim()) { toast.error('Texto da evolução é obrigatório'); return; }
    setSalvandoEvolucao(true);
    try {
      await api.post('/fisioterapia/evolucoes', {
        internacao_id: selectedPaciente.internacao_id,
        especialidade: activeTab,
        texto: novaEvolucao.texto,
        data_sessao: novaEvolucao.data_sessao,
      });
      toast.success('Evolução registrada');
      setNovaEvolucao({ texto: '', data_sessao: new Date().toISOString().split('T')[0] });
      fetchEvolucoes(selectedPaciente.internacao_id);
      fetchPacientes();
      fetchAlertas();
    } catch { toast.error('Erro ao salvar evolução'); }
    finally { setSalvandoEvolucao(false); }
  };

  const handleEditarEvolucao = async () => {
    if (!editingEvolucao?.texto.trim()) return;
    try {
      await api.put(`/fisioterapia/evolucoes/${editingEvolucao.id}`, {
        texto: editingEvolucao.texto,
        data_sessao: editingEvolucao.data_sessao,
      });
      toast.success('Evolução atualizada');
      setEditingEvolucao(null);
      fetchEvolucoes(selectedPaciente.internacao_id);
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handleExcluirEvolucao = async (id) => {
    if (!window.confirm('Excluir esta evolução?')) return;
    try {
      await api.delete(`/fisioterapia/evolucoes/${id}`);
      toast.success('Evolução excluída');
      fetchEvolucoes(selectedPaciente.internacao_id);
      fetchPacientes();
    } catch { toast.error('Erro ao excluir'); }
  };

  const filtered = data.pacientes.filter(p => {
    const matchSearch = !searchTerm ||
      p.paciente_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.diagnostico_principal || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchProf = !profissionalFilter || p.profissional_id === profissionalFilter;
    return matchSearch && matchProf;
  });

  const currentSpec = ESPECIALIDADES.find(e => e.key === activeTab);

  const formatDate = (d) => {
    if (!d || d.length < 10) return '-';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); }
    catch { return d; }
  };

  const alertasPorPaciente = {};
  alertas.forEach(a => { alertasPorPaciente[a.internacao_id] = a; });

  return (
    <Layout>
      <div className="space-y-6 p-4 sm:p-6" data-testid="fisioterapia-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Activity className="h-6 w-6 text-green-600" />
              Especialidades Terapêuticas
            </h1>
            <p className="text-muted-foreground">Pacientes com necessidade de acompanhamento por especialidade</p>
          </div>
        </div>

        {/* Specialty tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedPaciente(null); setProfissionalFilter(''); }} className="space-y-4">
          <TabsList>
            {ESPECIALIDADES.map(esp => (
              <TabsTrigger key={esp.key} value={esp.key} data-testid={`tab-${esp.key}`}>
                <Activity className={`h-4 w-4 mr-2 ${esp.color}`} />
                {esp.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {ESPECIALIDADES.map(esp => (
            <TabsContent key={esp.key} value={esp.key} className="space-y-4">
              {activeTab === esp.key && <AlertaPrimeirosAtendimentos especialidade={esp.key} />}
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold" data-testid={`total-${esp.key}`}>{data.total}</p>
                  <p className="text-xs text-muted-foreground">Pacientes com {esp.label}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {data.pacientes.filter(p => ['ativo_estavel', 'em_tratamento'].includes(p.status_kanban)).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Ativos / Em Tratamento</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">
                    {alertas.length}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Sessões Pendentes
                  </p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {data.pacientes.reduce((s, p) => s + (p.total_sessoes || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total de Sessões</p>
                </CardContent></Card>
              </div>

              {/* Alerts banner */}
              {alertas.length > 0 && (
                <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-900/10">
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-400">
                          {alertas.length} paciente(s) com sessão pendente
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {alertas.slice(0, 5).map(a => (
                            <Badge key={a.internacao_id} variant="outline" className="border-amber-400 text-amber-700 text-xs">
                              {a.paciente_nome} — {a.dias_sem_sessao}d sem sessão
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search + Professional filter */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por nome ou diagnóstico..." className="pl-9"
                        data-testid="input-search-fisio" />
                    </div>
                    {profissionais.length > 0 && (
                      <div className="flex items-center gap-2 sm:w-[280px]" data-testid="filter-profissional">
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <select
                          value={profissionalFilter}
                          onChange={(e) => setProfissionalFilter(e.target.value)}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="select-filter-profissional"
                        >
                          <option value="">Todos os profissionais</option>
                          {profissionais.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Content: Patient list + Evolution panel */}
              <div className={`grid gap-4 ${selectedPaciente ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {/* Patient list */}
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : filtered.length > 0 ? (
                  <Card>
                    <CardContent className="pt-0 px-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Paciente</TableHead>
                            <TableHead className="w-28 hidden sm:table-cell">Internação</TableHead>
                            <TableHead className="w-20 hidden md:table-cell">Sessões</TableHead>
                            <TableHead className="w-28">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filtered.map((p) => {
                            const alerta = alertasPorPaciente[p.internacao_id];
                            const isSelected = selectedPaciente?.internacao_id === p.internacao_id;
                            return (
                              <TableRow key={p.internacao_id}
                                className={`cursor-pointer transition-colors ${isSelected ? 'bg-accent' : 'hover:bg-accent/50'}`}
                                onClick={() => setSelectedPaciente(isSelected ? null : p)}
                                data-testid={`row-paciente-${p.internacao_id}`}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {alerta && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                                    <div>
                                      <p className="font-medium text-sm">{p.paciente_nome}</p>
                                      <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.diagnostico_principal || '-'}</p>
                                      {p.profissional_nome && (
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                          <User className="h-3 w-3" />{p.profissional_nome}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs hidden sm:table-cell">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                    {formatDate(p.data_internacao)}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center hidden md:table-cell">
                                  <Badge variant="secondary" className="text-xs">{p.total_sessoes || 0}</Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col gap-1">
                                    <Badge variant="outline" className="text-xs w-fit">
                                      {STATUS_LABELS[p.status_kanban] || p.status_kanban}
                                    </Badge>
                                    {alerta && (
                                      <span className="text-[10px] text-amber-600 font-medium">
                                        {alerta.dias_sem_sessao}d sem sessão
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p className="font-medium">Nenhum paciente com {esp.label}</p>
                      <p className="text-sm">Marque a especialidade na internação do paciente para que ele apareça aqui</p>
                    </CardContent>
                  </Card>
                )}

                {/* Evolution Panel */}
                {selectedPaciente && (
                  <Card className="lg:sticky lg:top-20" data-testid="painel-evolucao">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Evolução — {selectedPaciente.paciente_nome}
                        </CardTitle>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => navigate(`/pacientes/${selectedPaciente.paciente_id}/internacao/${selectedPaciente.internacao_id}`)}
                            title="Ver prontuário"
                          >
                            <Stethoscope className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => setSelectedPaciente(null)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* New evolution form */}
                      <div className="space-y-2 p-3 bg-muted/40 rounded-lg">
                        <Label className="text-xs font-medium">Nova Evolução</Label>
                        <div className="flex gap-2">
                          <Input type="date" className="w-36 text-sm"
                            value={novaEvolucao.data_sessao}
                            onChange={e => setNovaEvolucao(prev => ({ ...prev, data_sessao: e.target.value }))}
                            data-testid="input-data-sessao"
                          />
                          <Badge variant="outline" className={`${esp.border} text-xs shrink-0`}>{esp.label}</Badge>
                        </div>
                        <Textarea
                          placeholder="Descreva a evolução da sessão..."
                          value={novaEvolucao.texto}
                          onChange={e => setNovaEvolucao(prev => ({ ...prev, texto: e.target.value }))}
                          rows={3}
                          className="text-sm"
                          data-testid="input-evolucao-texto"
                        />
                        <Button size="sm" onClick={handleCriarEvolucao} disabled={salvandoEvolucao}
                          className="gap-1" data-testid="btn-salvar-evolucao">
                          {salvandoEvolucao ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                          Registrar Sessão
                        </Button>
                      </div>

                      {/* Evolution list */}
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {loadingEvolucoes ? (
                          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
                        ) : evolucoes.length > 0 ? (
                          evolucoes.map(ev => (
                            <div key={ev.id} className="p-3 border rounded-lg space-y-1 text-sm" data-testid={`evolucao-${ev.id}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-3 w-3 text-muted-foreground" />
                                  <span className="font-medium text-xs">{formatDate(ev.data_sessao)}</span>
                                  <span className="text-xs text-muted-foreground">por {ev.profissional_nome}</span>
                                </div>
                                <div className="flex gap-0.5">
                                  <Button variant="ghost" size="icon" className="h-6 w-6"
                                    onClick={(e) => { e.stopPropagation(); setEditingEvolucao({ ...ev }); }}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700"
                                    onClick={(e) => { e.stopPropagation(); handleExcluirEvolucao(ev.id); }}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-muted-foreground whitespace-pre-wrap">{ev.texto}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-xs text-muted-foreground py-4">Nenhuma evolução registrada</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit Evolution Dialog */}
        <Dialog open={!!editingEvolucao} onOpenChange={() => setEditingEvolucao(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Editar Evolução</DialogTitle></DialogHeader>
            {editingEvolucao && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Data da Sessão</Label>
                  <Input type="date" value={editingEvolucao.data_sessao || ''}
                    onChange={e => setEditingEvolucao(prev => ({ ...prev, data_sessao: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>Evolução</Label>
                  <Textarea value={editingEvolucao.texto} rows={4}
                    onChange={e => setEditingEvolucao(prev => ({ ...prev, texto: e.target.value }))} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingEvolucao(null)}>Cancelar</Button>
              <Button onClick={handleEditarEvolucao}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Fisioterapia;
